import { useEffect, useState } from "react";
import { OrdersService } from "../../../../../services/orders.service";
import { InvoiceService } from "../../../../../services/invoice.service";
import InputComponent from "../../../../shared/input/InputComponent";
import SelectComponent from "../../../../shared/select/SelectComponent";
import styles from "./EditInvoie.module.scss";
import fileimg from "../../../../../assets/images/packages/FilePlus.svg";
import x_icon from "../../../../../assets/images/packages/X.svg";
import MainButton from "../../../../shared/button/MainButton";

interface UploadInvoieProps {
  trackId: string;
  packId: number | null;
}

interface OrdersType {
  name: string;
  name_az: string;
  title: string;
  id: number;
  currency: string;
}

interface InvoicePayload {
  seller_id?: number | null;
  category_id?: number | null;
  currency_id?: number | null;
  other_seller?: string;
  price?: string;
  productName?: string;
  invoice?: File | null;
}

const UploadInvoie = ({ trackId, packId }: UploadInvoieProps) => {
  const [categories, setCategories] = useState<OrdersType[]>([]);
  const [sellers, setSellers] = useState<OrdersType[]>([]);
  const [currencies, setCurrencies] = useState<OrdersType[]>([]);
  const [existingInvoice, setExistingInvoice] = useState<string | null>(null);
  const [formData, setFormData] = useState<InvoicePayload>({
    seller_id: null,
    category_id: null,
    currency_id: null,
    other_seller: "",
    price: "",
    productName: "",
    invoice: null,
  });

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [sellersRes, categoriesRes, currenciesRes] = await Promise.all([
          OrdersService.getAllSellers(),
          OrdersService.getAllStoreCategory(),
          OrdersService.getAllStoreCurrency(),
        ]);

        setSellers(sellersRes.data.sellers);
        setCategories(categoriesRes.data.categories);
        setCurrencies(currenciesRes.data.currencies);

        if (packId) {
          const response = await InvoiceService.getPackagesUpdate(packId);
          if (response.data) {
            const packageData = response.data.original;
            setFormData({
              seller_id: packageData.seller_id || null,
              category_id: packageData.category_id || null,
              currency_id: packageData.currency_id || null,
              other_seller: packageData.other_seller || "",
              price: packageData.price || "",
              productName: packageData.title || "",
              invoice: null,
            });
            if (packageData.invoice_doc) {
              setExistingInvoice(packageData.invoice_doc);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching initial data:", error);
      }
    };

    fetchInitialData();
  }, [packId]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name: string, value: string | number | null) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value === "" ? null : value,
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      if (existingInvoice || formData.invoice) {
        // Prevent new upload if there's already a file
        return;
      }
      setFormData((prev) => ({ ...prev, invoice: e.target.files![0] }));
    }
  };

  const handleRemoveFile = () => {
    if (existingInvoice) {
      setExistingInvoice(null);
    }
    setFormData((prev) => ({ ...prev, invoice: null }));
    // Reset file input
    const fileInput = document.getElementById(
      "file-upload"
    ) as HTMLInputElement;
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const handleSave = async () => {
    if (!packId) return;
    const formDataToSend = new FormData();
    if (formData.seller_id !== null && formData.seller_id !== undefined) {
      formDataToSend.append("seller_id", formData.seller_id.toString());
    }
    if (formData.category_id !== null && formData.category_id !== undefined) {
      formDataToSend.append("category_id", formData.category_id.toString());
    }
    if (formData.currency_id !== null && formData.currency_id !== undefined) {
      formDataToSend.append("currency_id", formData.currency_id.toString());
    }
    if (formData.other_seller) {
      formDataToSend.append("other_seller", formData.other_seller);
    }
    if (formData.price) {
      formDataToSend.append("price", formData.price);
    }
    if (formData.productName) {
      formDataToSend.append("productName", formData.productName);
    }
    if (formData.invoice) {
      formDataToSend.append("invoice", formData.invoice);
    }
    try {
      const response = await InvoiceService.uploadInvoice(
        packId,
        formDataToSend
      );
    } catch (error) {
    }
  };
  return (
    <div className={styles.invoice_doc}>
      <div className={styles.upload_invoice}>
        <InputComponent
          label="Tracking code"
          type="text"
          placeholder="Tracking code"
          name="TrackId"
          disabled
          value={trackId}
        />

        <div className={styles.upload_inputs}>
          <SelectComponent
            name="seller_id"
            label="Mağaza"
            options={[
              { label: "Seçilməyib", value: "" },
              ...sellers.map((seller) => ({
                label: seller.title,
                value: seller.id,
              })),
            ]}
            onChange={(value) => handleSelectChange("seller_id", Number(value))}
            selectedValue={formData.seller_id ?? ""}
          />
        </div>

        <div className={styles.upload_inputs}>
          <SelectComponent
            name="category_id"
            label="Kategoriya"
            options={[
              { label: "Seçilməyib", value: "" },
              ...categories.map((category) => ({
                label: category.name_az,
                value: category.id,
              })),
            ]}
            onChange={(value) =>
              handleSelectChange("category_id", Number(value))
            }
            selectedValue={formData.category_id ?? ""}
          />
        </div>

        <div className={styles.upload_inputs}>
          <InputComponent
            label="Digər mağaza"
            type="text"
            placeholder="Mağaza adı"
            name="other_seller"
            value={formData.other_seller}
            onChange={handleInputChange}
          />
        </div>

        <div className={styles.upload_inputs}>
          <InputComponent
            label="Məhsulun adı"
            type="text"
            placeholder="Məhsulun adı"
            name="productName"
            value={formData.productName}
            onChange={handleInputChange}
          />
        </div>

        <div className={styles.upload_inputs}>
          <SelectComponent
            name="currency_id"
            label="Valyuta"
            options={[
              { label: "Seçilməyib", value: "" },
              ...currencies.map((currency) => ({
                label: currency.name,
                value: currency.id,
              })),
            ]}
            onChange={(value) =>
              handleSelectChange("currency_id", Number(value))
            }
            selectedValue={formData.currency_id ?? ""}
          />
        </div>

        <div className={styles.upload_inputs}>
          <InputComponent
            label="Məhsulun qiyməti"
            type="text"
            placeholder="Məhsulun qiyməti"
            name="price"
            value={formData.price}
            onChange={handleInputChange}
          />
        </div>
      </div>

      <div className={styles.upload_invoice}>
        <div className={styles.files}>
          <div className={styles.custom_file_upload}>
            <label>
              <input
                id="file-upload"
                type="file"
                accept="image/*,.pdf,.doc,.docx"
                onChange={handleFileChange}
                disabled={!!(existingInvoice || formData.invoice)}
              />
              <div className={styles.file_inp_text}>
                <img src={fileimg} alt="" />
                <p>İnvoys yüklə</p>
              </div>
            </label>
          </div>
          {(existingInvoice || formData.invoice) && (
            <div className={styles.uploaded_file}>
              <div className={styles.file_inp_text}>
                <p>
                  {formData.invoice
                    ? formData.invoice.name
                    : existingInvoice?.substring(0, 20)}
                </p>
                <div className={styles.x_icon} onClick={handleRemoveFile}>
                  <img src={x_icon} alt="x" />
                </div>
              </div>
            </div>
          )}
        </div>
        <MainButton
          onClick={handleSave}
          className={styles.invoice_btn}
          type="button"
          text="Yadda saxla"
        />
      </div>
    </div>
  );
};

export default UploadInvoie;
